import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { captureAnalytics } from '../../actions/preload';

const PREVIEW_URL = '/api/v1/referral_count_preview';
const COUNTS_URL = '/api/v1/receivers/referral_status_counts';

const ReferralCountPreview = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    const interceptor = axios.interceptors.response.use((response) => {
      if (mounted.current && response.config?.url?.split('?')[0] === COUNTS_URL) {
        const path = response.headers['x-referral-counts-path'];
        const fallback = response.headers['x-referral-counts-fallback'];
        const mode = response.headers['x-referral-counts-mode'];
        setLastRead(path ? { path, fallback, mode } : null);
      }
      return response;
    });
    // One request per impersonation context, canceled when this banner unmounts.
    axios.get(PREVIEW_URL, { signal: controller.signal }).then(({ data }) => {
      if (mounted.current && !controller.signal.aborted) setSettings(data);
    }).catch(() => {
      if (mounted.current) setSettings(null);
    });
    return () => {
      mounted.current = false;
      controller.abort();
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const changeMode = async (event) => {
    const mode = event.target.value;
    setSaving(true);
    setError(false);
    try {
      await axios.patch(PREVIEW_URL, { mode, context: settings.context }, {
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      });
      if (!mounted.current) return;
      captureAnalytics('referral_count_preview_changed', { mode });
      window.location.reload();
    } catch {
      if (mounted.current) {
        // Successful saves stay disabled until navigation; failed saves retry.
        setSaving(false);
        setError(true);
      }
    }
  };

  if (!settings) return null;

  let readLabel = 'Waiting for counts';
  if (lastRead?.path === 'projection') readLabel = 'Last counts: new';
  if (lastRead?.path === 'legacy') {
    readLabel = lastRead.fallback && !['feature_disabled', 'preview_legacy'].includes(lastRead.fallback)
      ? 'Last counts: old (fallback)' : 'Last counts: old';
  }

  return (
    <span style={{
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    }}
    >
      <label htmlFor="referral-count-preview">
        Referral counts:
        {' '}
        <select id="referral-count-preview" value={lastRead?.mode || settings.mode} onChange={changeMode} disabled={saving}>
          <option value="normal">Normal</option>
          <option value="legacy">Old</option>
          <option value="projection">New</option>
        </select>
      </label>
      <span style={{ fontSize: '12px' }} title={lastRead?.fallback || ''}>{saving ? 'Reloading…' : readLabel}</span>
      {error && <span role="alert">Couldn’t switch counts. Reload and try again.</span>}
    </span>
  );
};

export default ReferralCountPreview;
